import { Button, Center, Container, Stack, TextInput, Text } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const inputSchema = z.object({
  usageAlert: z
    .number({ message: '必須項目です。金額を入力してください' })
    .int('整数を入力してください')
    .and(z.union([z.number().min(1000, '1000以上の値を入力してください'), z.literal(0)])),
  usageLimit: z
    .number({ message: '数字を入力してください' })
    .int('整数を入力してください')
    .and(z.union([z.number().min(1000, '1000以上の値を入力してください'), z.literal(0)])),
});
export type UpdateLimitInputs = z.infer<typeof inputSchema>;

type FormProps = {
  currency: string;
  onSubmit: (data: UpdateLimitInputs) => void;
  defaultValues: UpdateLimitInputs;
};

export function UpdateLimitForm({ currency, onSubmit, defaultValues }: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors, dirtyFields },
    watch,
  } = useForm<UpdateLimitInputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues,
  });
  const [usageAlert, usageLimit] = watch(['usageAlert', 'usageLimit']);
  const isAlertLessThanLimit = useMemo(
    () => usageLimit === 0 || usageAlert <= usageLimit,
    [usageAlert, usageLimit]
  );
  const isReady = Object.keys(dirtyFields).length > 0 && isValid;

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Center>
          <Stack w="100%">
            <TextInput
              label="予算アラート"
              {...register('usageAlert', { valueAsNumber: true })}
              leftSection={currency === 'JPY' ? '¥' : '$'}
              error={errors.usageAlert?.message}
            />
            <Text size="xs">
              課金額がこの値に到達すると、テナント管理者のアドレスにアラートメールが送られます。0
              に設定すると無効になります。
              <br />
              現在の課金額よりも小さな値に設定した場合、アラートは送信されません。
            </Text>
            <TextInput
              label="予算上限"
              {...register('usageLimit', { valueAsNumber: true })}
              leftSection={currency === 'JPY' ? '¥' : '$'}
              error={errors.usageLimit?.message}
            />
            <Text size="xs">
              課金額がこの値に到達すると、API の実行ができなくなります。0
              に設定すると無効になります。
            </Text>
            {isReady && !isAlertLessThanLimit && (
              <Text c="red.6" size="xs">
                予算アラートは、予算上限より小さな値を設定してください。
              </Text>
            )}
            <Button type="submit" size="sm" disabled={!isReady || !isAlertLessThanLimit} mt="lg">
              保存
            </Button>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
